<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const store = useStore();
const getters = useStoreGetters();

onMounted(() => {
  store.dispatch('contactFieldDefinitions/fetchAll');
});

const allFields = computed(() => getters['contactFieldDefinitions/getAll'].value);
const uiFlags   = computed(() => getters['contactFieldDefinitions/uiFlags'].value);

const searchQuery = ref('');
const expandedId  = ref(null);
const draggingIdx = ref(null);
const dragOverIdx = ref(null);

const filteredFields = computed(() =>
  allFields.value.filter(f =>
    f.label.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
);

// ── Grouped view ───────────────────────────────────────────────────────────────
const groupedFields = computed(() => {
  const groups = {};
  filteredFields.value.forEach((f, idx) => {
    const g = f.group_name || 'General';
    if (!groups[g]) groups[g] = [];
    groups[g].push({ field: f, idx });
  });
  return groups;
});

const FIELD_TYPES = [
  { id: 'text',        label: 'Text',        icon: 'i-lucide-type' },
  { id: 'number',      label: 'Number',      icon: 'i-lucide-hash' },
  { id: 'date',        label: 'Date',        icon: 'i-lucide-calendar' },
  { id: 'link',        label: 'Link',        icon: 'i-lucide-link' },
  { id: 'list',        label: 'List',        icon: 'i-lucide-list' },
  { id: 'checkbox',    label: 'Checkbox',    icon: 'i-lucide-check-square' },
  { id: 'auto_number', label: 'Auto-Number', icon: 'i-lucide-hash' },
  { id: 'group',       label: 'Group',       icon: 'i-lucide-building-2' },
];
const TYPE_ICON = Object.fromEntries(FIELD_TYPES.map(t => [t.id, t.icon]));

const NUMBER_FORMATS = [
  { id: 'comma_dot',   label: '9,999,999.99  — North America / UK' },
  { id: 'dot_comma',   label: '9.999.999,99  — South America / Europe' },
  { id: 'indian',      label: '9,99,99,999.99 — India' },
  { id: 'space_comma', label: '9 999 999,99  — Most of Europe' },
];

// ── Dialog ─────────────────────────────────────────────────────────────────────
const dialogRef    = ref(null);
const editingField = ref(null);
const deleting     = ref(null);

const emptyForm = () => ({
  label: '',
  type: 'text',
  options: [],
  newOption: '',
  groupName: '',
  numberFormat: 'comma_dot',
  anPrefix: '',
  anDigits: 4,
  anStarting: 1,
});
const form = ref(emptyForm());

const openAdd = () => {
  editingField.value = null;
  form.value = emptyForm();
  dialogRef.value?.open();
};

const openEdit = field => {
  editingField.value = field;
  const cfg = field.field_config || {};
  form.value = {
    label: field.label,
    type: field.field_type,
    options: [...(field.options || [])],
    newOption: '',
    groupName: field.group_name || '',
    numberFormat: cfg.format || 'comma_dot',
    anPrefix: cfg.prefix || '',
    anDigits: cfg.digits || 4,
    anStarting: cfg.starting_number || 1,
  };
  dialogRef.value?.open();
};

const closePanel = () => { dialogRef.value?.close(); editingField.value = null; };

const addOption = () => {
  const v = form.value.newOption.trim();
  if (!v) return;
  if (form.value.options.includes(v)) { useAlert('Choice already exists'); return; }
  if (form.value.options.length >= 300) { useAlert('Maximum 300 choices allowed'); return; }
  form.value.options.push(v);
  form.value.newOption = '';
};
const removeOption = idx => form.value.options.splice(idx, 1);

const buildPayload = () => {
  const base = {
    label: form.value.label.trim(),
    field_type: form.value.type,
    options: form.value.type === 'list' ? form.value.options : [],
    enabled: true,
    group_name: form.value.groupName.trim() || null,
    field_config: {},
  };
  if (form.value.type === 'number') {
    base.field_config = { format: form.value.numberFormat };
  }
  if (form.value.type === 'auto_number') {
    base.field_config = {
      prefix: form.value.anPrefix.slice(0, 5),
      digits: Number(form.value.anDigits) || 4,
      starting_number: Number(form.value.anStarting) || 1,
    };
    base.read_only = true;
    base.required  = false;
  }
  return base;
};

const saveField = async () => {
  if (!form.value.label.trim()) return;
  try {
    if (editingField.value) {
      await store.dispatch('contactFieldDefinitions/update', { id: editingField.value.id, ...buildPayload() });
      useAlert('Field updated');
    } else {
      await store.dispatch('contactFieldDefinitions/create', buildPayload());
      useAlert('Field created');
    }
    closePanel();
  } catch (e) {
    useAlert(e?.message || 'Failed to save field');
  }
};

const deleteField = async field => {
  if (!confirm(`Delete "${field.label}"? It will be removed from the contact form.`)) return;
  deleting.value = field.id;
  try {
    await store.dispatch('contactFieldDefinitions/delete', field.id);
    useAlert('Field deleted');
    if (expandedId.value === field.id) expandedId.value = null;
  } catch {
    useAlert('Failed to delete field');
  } finally {
    deleting.value = null;
  }
};

// ── Toggle helpers ─────────────────────────────────────────────────────────────
const buildTogglePayload = (field, overrides) => ({
  id: field.id,
  label: field.label,
  field_type: field.field_type,
  options: field.options || [],
  enabled: field.enabled,
  required: field.required,
  quick_add: field.quick_add,
  read_only: field.read_only,
  unique_field: field.unique_field,
  group_name: field.group_name,
  field_config: field.field_config || {},
  ...overrides,
});

const toggleEnabled = async field => {
  try {
    await store.dispatch('contactFieldDefinitions/update', buildTogglePayload(field, { enabled: !field.enabled }));
  } catch { useAlert('Failed to update field'); }
};

const toggleRequired = async field => {
  const newRequired = !field.required;
  try {
    await store.dispatch('contactFieldDefinitions/update', buildTogglePayload(field, {
      required: newRequired,
      read_only: newRequired ? false : field.read_only,
    }));
  } catch { useAlert('Failed to update field'); }
};

const toggleQuickAdd = async field => {
  try {
    await store.dispatch('contactFieldDefinitions/update', buildTogglePayload(field, { quick_add: !field.quick_add }));
  } catch { useAlert('Failed to update field'); }
};

const toggleReadOnly = async field => {
  const newReadOnly = !field.read_only;
  try {
    await store.dispatch('contactFieldDefinitions/update', buildTogglePayload(field, {
      read_only: newReadOnly,
      required: newReadOnly ? false : field.required,
    }));
  } catch { useAlert('Failed to update field'); }
};

const toggleUnique = async field => {
  try {
    await store.dispatch('contactFieldDefinitions/update', buildTogglePayload(field, { unique_field: !field.unique_field }));
  } catch { useAlert('Failed to update field'); }
};

// ── Drag & drop ────────────────────────────────────────────────────────────────
const onDragStart = (e, idx) => {
  draggingIdx.value = idx;
  e.dataTransfer.effectAllowed = 'move';
};

const onDragOver = (e, idx) => {
  e.preventDefault();
  dragOverIdx.value = idx;
};

const onDragLeave = () => { dragOverIdx.value = null; };

const onDrop = async (e, toIdx) => {
  e.preventDefault();
  const fromIdx = draggingIdx.value;
  draggingIdx.value = null;
  dragOverIdx.value = null;
  if (fromIdx === null || fromIdx === toIdx) return;

  const fields = [...allFields.value];
  const [moved] = fields.splice(fromIdx, 1);
  fields.splice(toIdx, 0, moved);
  store.commit('contactFieldDefinitions/SET_RECORDS', fields);
  await store.dispatch('contactFieldDefinitions/reorder', fields.map(f => f.id));
};

const onDragEnd = () => { draggingIdx.value = null; dragOverIdx.value = null; };
</script>

<template>
  <div class="mx-auto max-w-4xl px-6 py-8">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">Contact Fields</h1>
        <p class="text-sm text-n-slate-9 mt-0.5">
          Define extra fields that appear in the contact form — alongside name, email, and phone.
        </p>
      </div>
      <button
        class="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-n-slate-11 border border-n-weak rounded-lg hover:bg-n-alpha-2 transition-colors"
        @click="openAdd"
      >
        <span class="i-lucide-plus size-3.5" />
        Add field
      </button>
    </div>

    <!-- Search -->
    <div class="mb-4">
      <div class="relative">
        <span class="i-lucide-search absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-9" />
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search fields..."
          class="w-full pl-9 pr-3 py-2 text-sm border border-n-weak rounded-lg bg-transparent text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-n-alpha-2"
        />
      </div>
    </div>

    <!-- Loading -->
    <div v-if="uiFlags.isFetching" class="flex items-center justify-center py-16">
      <span class="i-lucide-loader-2 animate-spin size-6 text-woot-500" />
    </div>

    <div v-else>
      <!-- Groups -->
      <div
        v-for="(entries, groupName) in groupedFields"
        :key="groupName"
        class="mb-6"
      >
        <!-- Group header -->
        <div class="flex items-center gap-2 mb-3 px-1">
          <span class="i-lucide-folder size-4 text-yellow-500" />
          <span class="text-sm font-medium text-n-slate-12">{{ groupName }}</span>
          <span class="text-xs text-n-slate-8 ml-1">({{ entries.length }})</span>
        </div>

        <!-- Field rows -->
        <div class="space-y-1.5">
          <div
            v-for="{ field, idx } in entries"
            :key="field.id"
            :draggable="!searchQuery"
            class="rounded-xl border transition-all"
            :class="[
              dragOverIdx === idx ? 'border-woot-400 bg-woot-50/30 dark:bg-woot-900/10' : 'border-n-weak bg-white dark:bg-n-solid-1',
              draggingIdx === idx ? 'opacity-50' : 'opacity-100',
              !field.enabled ? 'opacity-60' : '',
            ]"
            @dragstart="onDragStart($event, idx)"
            @dragover="onDragOver($event, idx)"
            @dragleave="onDragLeave"
            @drop="onDrop($event, idx)"
            @dragend="onDragEnd"
          >
            <!-- Main row -->
            <div class="flex items-center gap-3 px-3 py-3">
              <!-- Drag handle -->
              <span
                class="i-lucide-grip-vertical size-4 text-n-slate-6 cursor-grab active:cursor-grabbing shrink-0"
                :class="searchQuery ? 'opacity-0 pointer-events-none' : ''"
              />

              <!-- Type icon -->
              <span :class="TYPE_ICON[field.field_type] || 'i-lucide-type'" class="size-4 text-n-slate-9 shrink-0" />

              <!-- Field name -->
              <span class="flex-1 text-sm font-medium text-n-blue-11 min-w-0 truncate">{{ field.label }}</span>

              <!-- Auto-number badge -->
              <span v-if="field.field_type === 'auto_number'" class="text-xs px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-9 shrink-0">
                auto
              </span>

              <!-- Required -->
              <label
                class="flex items-center gap-1.5 text-xs cursor-pointer select-none"
                :class="field.read_only ? 'text-n-slate-6 pointer-events-none' : 'text-n-slate-9'"
                @click.stop
              >
                <input
                  type="checkbox"
                  :checked="field.required"
                  :disabled="field.read_only || field.field_type === 'auto_number'"
                  class="w-3.5 h-3.5 rounded accent-woot-500 cursor-pointer disabled:opacity-40"
                  @change="toggleRequired(field)"
                />
                Required
              </label>

              <!-- Quick-add -->
              <label class="flex items-center gap-1.5 text-xs text-n-slate-9 cursor-pointer select-none" @click.stop>
                <input
                  type="checkbox"
                  :checked="field.quick_add || field.required"
                  :disabled="field.required"
                  class="w-3.5 h-3.5 rounded accent-woot-500 cursor-pointer disabled:opacity-40"
                  @change="toggleQuickAdd(field)"
                />
                Quick-add
              </label>

              <!-- Read-only -->
              <label
                class="flex items-center gap-1.5 text-xs cursor-pointer select-none"
                :class="field.required ? 'text-n-slate-6 pointer-events-none' : 'text-n-slate-9'"
                @click.stop
              >
                <input
                  type="checkbox"
                  :checked="field.read_only || field.field_type === 'auto_number'"
                  :disabled="field.required || field.field_type === 'auto_number'"
                  class="w-3.5 h-3.5 rounded accent-woot-500 cursor-pointer disabled:opacity-40"
                  @change="toggleReadOnly(field)"
                />
                Read-only
              </label>

              <!-- Unique -->
              <label class="flex items-center gap-1.5 text-xs text-n-slate-9 cursor-pointer select-none" @click.stop>
                <input
                  type="checkbox"
                  :checked="field.unique_field"
                  class="w-3.5 h-3.5 rounded accent-woot-500 cursor-pointer"
                  @change="toggleUnique(field)"
                />
                Unique
              </label>

              <!-- Expand chevron -->
              <button
                class="p-1 rounded hover:bg-n-alpha-2 transition-colors text-n-slate-9"
                @click="expandedId = expandedId === field.id ? null : field.id"
              >
                <span
                  class="i-lucide-chevron-down size-4 transition-transform duration-200"
                  :class="expandedId === field.id ? 'rotate-180' : ''"
                />
              </button>
            </div>

            <!-- Expanded actions -->
            <div
              v-if="expandedId === field.id"
              class="flex items-center gap-2 px-4 pb-3 pt-0 border-t border-n-weak/50"
            >
              <span class="text-xs text-n-slate-8 font-mono mr-2">{{ field.field_key }}</span>
              <div class="flex-1" />
              <button
                class="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-n-slate-11 border border-n-weak rounded-lg hover:bg-n-alpha-2 transition-colors"
                @click="openEdit(field)"
              >
                <span class="i-lucide-pencil size-3" />
                Edit field
              </button>
              <button
                class="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-n-slate-11 border border-n-weak rounded-lg hover:bg-n-alpha-2 transition-colors"
                @click="toggleEnabled(field)"
              >
                <span :class="field.enabled ? 'i-lucide-eye-off' : 'i-lucide-eye'" class="size-3" />
                {{ field.enabled ? 'Hide field' : 'Show field' }}
              </button>
              <button
                class="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-red-500 border border-red-200 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                :disabled="deleting === field.id"
                @click="deleteField(field)"
              >
                <span :class="deleting === field.id ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-trash-2'" class="size-3" />
                Delete field
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-if="!filteredFields.length" class="flex flex-col items-center justify-center py-16 text-center">
        <span class="i-lucide-users size-10 text-n-slate-6 mb-3" />
        <p class="text-sm font-medium text-n-slate-9">
          {{ searchQuery ? 'No fields match your search' : 'No contact fields yet' }}
        </p>
        <p v-if="!searchQuery" class="text-xs text-n-slate-8 mt-1">Fields you add here appear in the contact form.</p>
        <button
          v-if="!searchQuery"
          class="mt-4 flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-woot-600 border border-woot-300 rounded-lg hover:bg-woot-50 dark:hover:bg-woot-900/20 transition-colors"
          @click="openAdd"
        >
          <span class="i-lucide-plus size-3.5" /> Add your first field
        </button>
      </div>
    </div>

    <!-- Add / Edit dialog -->
    <Dialog
      ref="dialogRef"
      :title="editingField ? 'Edit Contact Field' : 'Add Contact Field'"
      :confirm-button-label="uiFlags.isSaving ? 'Saving…' : (editingField ? 'Update' : 'Create Field')"
      cancel-button-label="Cancel"
      :disable-confirm-button="!form.label.trim() || uiFlags.isSaving"
      :is-loading="uiFlags.isSaving"
      @confirm="saveField"
      @close="closePanel"
    >
      <div class="flex flex-col gap-4">
        <!-- Label -->
        <div>
          <label class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1.5">Field Label *</label>
          <input
            v-model="form.label"
            type="text"
            placeholder="e.g. Department, Job Title, Employee ID…"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak dark:bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
            @keydown.enter.prevent="saveField"
          />
        </div>

        <!-- Group -->
        <div>
          <label class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1.5">Group / Section</label>
          <input
            v-model="form.groupName"
            type="text"
            placeholder="e.g. Work Info, Social Media… (leave blank for General)"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak dark:bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
          />
        </div>

        <!-- Field Type -->
        <div>
          <label class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1.5">Field Type</label>
          <div class="grid grid-cols-4 gap-2">
            <button
              v-for="ft in FIELD_TYPES" :key="ft.id"
              class="flex items-center gap-1.5 px-2 py-2 text-xs rounded-lg border transition-colors"
              :class="form.type === ft.id
                ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/20 text-woot-700 dark:text-woot-300'
                : 'border-n-weak text-n-slate-11 hover:border-woot-300 disabled:opacity-40 disabled:cursor-not-allowed'"
              :disabled="!!editingField"
              @click="!editingField && (form.type = ft.id)"
            >
              <span :class="ft.icon" class="size-3.5 shrink-0" />
              {{ ft.label }}
            </button>
          </div>
          <p v-if="editingField" class="mt-1 text-xs text-n-slate-9">Field type cannot be changed after creation.</p>
        </div>

        <!-- Number format -->
        <div v-if="form.type === 'number'">
          <label class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1.5">Number Format</label>
          <div class="space-y-1.5">
            <label
              v-for="nf in NUMBER_FORMATS" :key="nf.id"
              class="flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer transition-colors"
              :class="form.numberFormat === nf.id
                ? 'border-woot-400 bg-woot-50/60 dark:bg-woot-900/20'
                : 'border-n-weak hover:border-n-slate-6'"
            >
              <input v-model="form.numberFormat" type="radio" :value="nf.id" class="accent-woot-500" />
              <span class="text-xs text-n-slate-12 font-mono">{{ nf.label }}</span>
            </label>
          </div>
        </div>

        <!-- Auto-number config -->
        <div v-if="form.type === 'auto_number'" class="space-y-3">
          <div class="p-3 bg-n-alpha-1 rounded-lg text-xs text-n-slate-9 border border-n-weak">
            Auto-Number fields are system-generated, unique, and read-only. The value is automatically assigned when a contact is created.
          </div>
          <div class="grid grid-cols-3 gap-3">
            <div>
              <label class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1">Prefix</label>
              <input
                v-model="form.anPrefix"
                type="text"
                maxlength="5"
                placeholder="e.g. CON"
                class="w-full px-2.5 py-1.5 text-sm rounded-lg border border-n-weak dark:bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
              />
              <p class="text-xs text-n-slate-8 mt-0.5">Max 5 chars</p>
            </div>
            <div>
              <label class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1">Digits</label>
              <input
                v-model.number="form.anDigits"
                type="number"
                min="1"
                max="10"
                class="w-full px-2.5 py-1.5 text-sm rounded-lg border border-n-weak dark:bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
              />
            </div>
            <div>
              <label class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1">Starts at</label>
              <input
                v-model.number="form.anStarting"
                type="number"
                min="1"
                class="w-full px-2.5 py-1.5 text-sm rounded-lg border border-n-weak dark:bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
              />
              <p v-if="editingField" class="text-xs text-n-slate-8 mt-0.5">Cannot reset counter</p>
            </div>
          </div>
          <p class="text-xs text-n-slate-9">
            Preview: <span class="font-mono text-woot-600">{{ (form.anPrefix || '').slice(0, 5) }}{{ String(form.anStarting || 1).padStart(form.anDigits || 4, '0') }}</span>
          </p>
        </div>

        <!-- List options -->
        <div v-if="form.type === 'list'">
          <label class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1.5">
            Choices
            <span class="normal-case font-normal text-n-slate-8 ml-1">({{ form.options.length }}/300)</span>
          </label>
          <div class="flex flex-wrap gap-1.5 mb-2 min-h-[1.5rem]">
            <span
              v-for="(opt, idx) in form.options" :key="idx"
              class="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-n-alpha-2 text-n-slate-12"
            >
              {{ opt }}
              <button class="hover:text-red-500 text-n-slate-9 ml-0.5" @click="removeOption(idx)">×</button>
            </span>
            <span v-if="!form.options.length" class="text-xs text-n-slate-9 italic">No choices yet</span>
          </div>
          <div class="flex gap-2">
            <input
              v-model="form.newOption" type="text" placeholder="Add a choice…"
              class="flex-1 px-2 py-1.5 text-xs rounded-lg border border-n-weak dark:bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
              @keydown.enter.prevent="addOption"
            />
            <button
              class="px-3 py-1.5 text-xs font-medium text-woot-600 border border-woot-200 rounded-lg hover:bg-woot-50 dark:hover:bg-woot-900/20"
              @click="addOption"
            >+ Add</button>
          </div>
        </div>
      </div>
    </Dialog>
  </div>
</template>
